import React, { useCallback } from "react";
import cn from "classnames";
import { CellContext } from "@tanstack/react-table";
import { t } from "i18next";
import { DownloadIcon } from "oneclick-component/src/icon";
import PopMenu, {
  PopMenuItem,
} from "oneclick-component/src/components/PopMenu";
import ProgressBar from "oneclick-component/src/components/ProgressBar";
import useGenerateShiftPdfWebSocket from "../../../hooks/useGenerateShiftPdfWebSocket";
import HireStatusPopup from "../HireStatusPopup";
import { useIsSuperAdmin } from "../../../hooks/role";
import { ExpiredShiftListRecord } from "./useExpiredShiftListTable";

interface FullTimeStatusSectionProps {
  record: ExpiredShiftListRecord;
}

const FullTimeStatusSection = (props: FullTimeStatusSectionProps) => {
  const { record } = props;

  return (
    <div>
      <ProgressBar
        max={record.fullTimeCount}
        count={record.hiredFullTimeCount}
        labelStyle="top"
        barSize="l"
        className={cn("mt-8", "mb-2")}
        title={t("shiftList.table.incident.hireStatus.fullTime")}
      />
    </div>
  );
};

export const ExpiredShiftListTableAction = ({
  row,
}: CellContext<ExpiredShiftListRecord, unknown>): React.ReactElement => {
  const triggerPdfGeneration = useGenerateShiftPdfWebSocket();
  const isSuperAdmin = useIsSuperAdmin();

  const onClickExportPdf = useCallback(() => {
    triggerPdfGeneration([row.original.id]);
  }, [row, triggerPdfGeneration]);

  const popMenuControlElementClass = useCallback(
    ({ open }: { open: boolean }) =>
      cn(
        "flex",
        "max-w-xs",
        "mx-auto",
        "items-center",
        "rounded-full",
        "bg-white",
        "text-sm",
        "focus:outline-none",
        "focus:ring-2",
        "focus:ring-primary-500",
        "focus:ring-offset-2",
        "p-3", // enlarge clickable area
        "-m-3", // enlarge clickable area
        open ? "fill-primary-500" : "fill-gray-500"
      ),
    []
  );

  const stopPropagate = useCallback((e?: React.MouseEvent<HTMLElement>) => {
    e?.stopPropagation();
  }, []);
  return (
    <div onClick={stopPropagate}>
      {isSuperAdmin ? (
        <></>
      ) : (
        <PopMenu
          // workaround cell padding asymmertic
          menuClass={cn("relative", "pr-1", "sm:pr-3")}
          controlElementClass={popMenuControlElementClass}
          controlElement={<DownloadIcon className={cn("w-5", "h-5")} />}
        >
          <div className="py-1">
            <PopMenuItem
              type="button"
              onClick={onClickExportPdf}
              className={"text-gray-700"}
              text={t("shiftList.tab.completed.exportMenu.pdf")}
            />
          </div>
        </PopMenu>
      )}
    </div>
  );
};

export const ExpiredShiftListTableHireStatus = ({
  row,
}: CellContext<ExpiredShiftListRecord, unknown>): React.ReactElement | null => {
  const { original } = row;
  const isIncident = original.shiftType === "INCIDENT";
  return (
    <HireStatusPopup
      shiftId={original.id}
      workingStationShortCode={original.hireStatus.workingStationShortCode}
      isIncident={isIncident}
      shiftRequests={original.hireStatus.hiredRequests}
      fulfillmentCount={original.hireStatus.fulfillmentCount}
      fulltimeCount={original.fullTimeCount}
    >
      <ProgressBar
        max={original.hireStatus.fulfillmentCount}
        count={original.hireStatus.hiredRequests.length}
        labelStyle="top"
        barSize="l"
        title={
          isIncident ? t("shiftList.table.incident.hireStatus.partTime") : ""
        }
      />
      {isIncident ? <FullTimeStatusSection record={original} /> : null}
    </HireStatusPopup>
  );
};
